import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { FaThLarge } from 'react-icons/fa';
import { MODULE_CONFIG } from '../../ModuleContext';
import ModulePopover from '../popovers/ModulePopover';
import * as colors from '../../../../config/colors';

const slideDown = keyframes`
   from {
      opacity: 0;
      transform: translateY(-6px);
   }
   to {
      opacity: 1;
      transform: translateY(0);
   }
`;

/**
 * Wrapper do dropdown inline (modo expandido).
 *
 * Precisa ser um styled-component próprio porque:
 *   1. O .module-popover do styled.js usa position:absolute calculando
 *      a partir do wrapper-pai (posicionamento lateral). Aqui queremos
 *      abrir abaixo do botão, precisamos redefinir posicionamento.
 *   2. Precisa de animação de fade-in, que o .module-popover original não tem.
 *   3. Os <button> internos precisam vencer o GlobalStyles.js.
 */
const InlineDropdown = styled.div`
   position: absolute;
   left: 0;
   right: 0;
   top: calc(100% + 4px);
   z-index: 50;
   padding: 6px 0;
   background: #fff;
   border: 1px solid #e5e7eb;
   border-radius: 10px;
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
   animation: ${slideDown} 180ms cubic-bezier(0.2, 0, 0, 1);

   .inline-dropdown-title {
      margin: 0 0 4px;
      padding: 0 14px;
      color: #9ca3af;
      font-size: 0.65rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
   }

   .inline-dropdown-item {
      /* Overrides contra GlobalStyles.js */
      background: transparent;
      color: #374151;
      font-weight: 500;
      padding: 8px 14px;
      border-radius: 0;

      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      font-size: 0.8rem;
      font-family: inherit;
      text-align: left;
      border: none;
      cursor: pointer;
      transition: background 140ms ease, color 140ms ease;

      &:hover {
         background: #f9fafb;
         color: ${colors.primaryDarkColor};
      }

      &.active {
         color: ${colors.primaryDarkColor};
         font-weight: 600;
      }
   }
`;

const TriggerButton = styled.button`
   /* Overrides contra GlobalStyles.js */
   background: #f9fafb;
   color: ${colors.primaryDarkColor};
   padding: 8px 12px;
   font-weight: 500;

   display: flex;
   align-items: center;
   justify-content: space-between;
   width: 100%;
   font-size: 0.8rem;
   font-family: inherit;
   text-align: left;
   border: 1px solid #e5e7eb;
   border-radius: 8px;
   cursor: pointer;
   transition: border-color 180ms ease, background 180ms ease;

   &:hover {
      background: #f3f4f6;
      border-color: ${colors.primaryDarkColor}40;
   }

   &.open {
      border-color: ${colors.primaryDarkColor}60;
      background: #fff;
   }

   .trigger-value {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }

   .trigger-chevron {
      color: #6b7280;
      font-size: 0.7rem;
      transition: transform 220ms ease;

      &.rotate {
         transform: rotate(180deg);
      }
   }

   .trigger-placeholder {
      color: #9ca3af;
      font-weight: 400;
   }
`;

/**
 * Seletor de módulo. Dois modos:
 *   expandido  → TriggerButton + InlineDropdown (styled components locais)
 *   collapsed  → botão ícone + ModulePopover via portal
 */
function ModuleSelector({
   availableModules,
   activeModule = null,
   onSelectModule,
   collapsed,
}) {
   const [dropdownOpen, setDropdownOpen] = useState(false);
   const [popoverOpen, setPopoverOpen] = useState(false);
   const wrapperRef = useRef(null);
   const moduleBtnRef = useRef(null);

   const activeConfig = activeModule ? MODULE_CONFIG[activeModule] : null;

   // Fecha dropdown inline ao clicar fora
   useEffect(() => {
      if (!dropdownOpen) return undefined;
      const handler = (e) => {
         if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setDropdownOpen(false);
         }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
   }, [dropdownOpen]);

   // Fecha tudo ao trocar entre collapsed/expandido
   useEffect(() => {
      setDropdownOpen(false);
      setPopoverOpen(false);
   }, [collapsed]);

   const handleSelect = (key) => {
      onSelectModule(key);
      setDropdownOpen(false);
   };

   // =========================================================================
   // COLLAPSED — botão ícone + popover lateral via portal
   // =========================================================================
   if (collapsed) {
      return (
         <div className="module-popover-wrapper">
            <button
               ref={moduleBtnRef}
               type="button"
               className="module-popover-btn"
               onClick={() => setPopoverOpen((prev) => !prev)}
               title="Selecionar módulo"
            >
               <FaThLarge size={16} />
            </button>

            {popoverOpen && (
               <ModulePopover
                  availableModules={availableModules}
                  activeModule={activeModule}
                  onSelect={onSelectModule}
                  onClose={() => setPopoverOpen(false)}
                  anchorRef={moduleBtnRef}
               />
            )}
         </div>
      );
   }

   // =========================================================================
   // EXPANDIDO — trigger + dropdown inline animado
   // =========================================================================
   return (
      <div
         className="module-selector-wrapper"
         ref={wrapperRef}
         style={{ position: 'relative' }}
      >
         <p className="module-selector-label">Módulo</p>

         <TriggerButton
            type="button"
            className={dropdownOpen ? 'open' : ''}
            onClick={() => setDropdownOpen((prev) => !prev)}
            title="Selecionar módulo"
         >
            <span className="trigger-value">
               {activeConfig ? (
                  <>
                     <span>{activeConfig.icon}</span>
                     <span>{activeConfig.label}</span>
                  </>
               ) : (
                  <span className="trigger-placeholder">Selecionar módulo...</span>
               )}
            </span>
            <span className={`trigger-chevron ${dropdownOpen ? 'rotate' : ''}`}>
               ▼
            </span>
         </TriggerButton>

         {dropdownOpen && (
            <InlineDropdown role="menu">

               <button
                  type="button"
                  role="menuitem"
                  className={`inline-dropdown-item ${!activeModule ? 'active' : ''}`}
                  onClick={() => handleSelect(null)}
               >
                  <span className="trigger-placeholder">Selecionar módulo...</span>
               </button>

               {availableModules.map(([key, config]) => {
                  const active = activeModule === key;
                  return (
                     <button
                        key={key}
                        type="button"
                        role="menuitem"
                        className={`inline-dropdown-item ${active ? 'active' : ''}`}
                        onClick={() => handleSelect(key)}
                     >
                        <span>{config.icon}</span>
                        <span style={{ flex: 1 }}>{config.label}</span>
                     </button>
                  );
               })}
            </InlineDropdown>
         )}

         {activeModule && !dropdownOpen && (
            <div
               className="module-badge"
               onClick={() => onSelectModule(null)}
               role="button"
               tabIndex={0}
               onKeyDown={(e) => {
                  if (e.key === 'Enter') onSelectModule(null);
               }}
               title="Limpar seleção"
            >
            </div>
         )}
      </div>
   );
}

ModuleSelector.propTypes = {
   availableModules: PropTypes.arrayOf(PropTypes.array).isRequired,
   activeModule: PropTypes.string,
   onSelectModule: PropTypes.func.isRequired,
   collapsed: PropTypes.bool.isRequired,
};

export default ModuleSelector;
